import { css } from '@emotion/react';
import { faMap } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type FormEvent, useCallback, useEffect, useMemo, useState,
} from 'react';
import { Accordion } from '~/_shared/baseComponents/accordion';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import {
  ButtonSize, ButtonStyle, computeButtonFontSize,
} from '~/_shared/baseComponents/buttons';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { LoaderType } from '~/_shared/baseComponents/loaders';
import { OutlinePanelComponent } from '~/_shared/baseComponents/outlinePanel/outlinePanel.component';
import { OutlinePanelOptions } from '~/_shared/baseComponents/outlinePanel/outlinePanel.constants';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { ModalFooterSampleExcelComponent } from '~/_shared/components/modalFooterSampleExcel/modalFooterSampleExcel.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { UploadStatus } from '~/_shared/components/uploadStatus/uploadStatus.component';
import {
  SAMPLE_SPREADSHEET_FILE_NAME, SampleSpreadsheetUrl,
} from '~/_shared/constants/sampleData';
import { useTranslation } from '~/_shared/utils/hooks';
import { useHookWithRefCallback } from '~/_shared/utils/hooks/useHookWithRefCallback';
import { isTextEmpty } from '~/_shared/utils/text/text.helpers';
import type { AppError } from '~/appError/appError.type';
import { AppErrorType } from '~/appError/appErrorType.enum';
import type { ModalProps } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import type { CreateLayeredMapProps } from '../layered/createLayeredMapModal/createLayeredMapModal.container';
import { SelectSpreadsheetWithErrorComponent } from '../selectSpreadsheet/withError/selectSpreadsheetWithError.component';
import { TextInputSpreadsheetDataComponent } from '../textInputSpreadsheetData/textInputSpreadsheetData.component';
import { DataSourceNotesComponent } from './dataSourceNotes/dataSourceNotes.component';
import { MapNameAndDescriptionComponent } from './mapNameAndDescription.component';
import { UploadMethodsComponent } from './uploadMethods/uploadMethods.component';
import type { useCreateMapFromInput } from './useCreateMapFromInput';

const LOADER_SIZE_OFFSET = 4;
const defaultButtonSize = ButtonSize.Medium;
const SAMPLE_MAP_DEFAULT_NAME = 'Sample Map';

export type UploadItem = {
  id: string;
  status: UploadStatus;
  file: File;
  progress: number;
};

const accordionContentStyle = css({
  padding: '2px 10px',
});

const uploadMethodSeparatorStyle = css({
  display: 'block',
  padding: '8px 0',
  textAlign: 'center',
  fontSize: 14,
  textTransform: 'uppercase',
});

const alertStyle = css({
  marginTop: 20,
});

const itemButtonStyle = css({
  textTransform: 'uppercase',
});

const fullWidthStyle = css({
  width: '100%',
});

const layeringWrapper = css({
  borderRadius: 4,
  overflow: 'hidden',
  position: 'relative',
});

type PrefillFormData = {
  mapName: string;
  mapDescription: string;
  textInputValue: string;
  isMapOverviewOpen: boolean;
  isDataSourceOpen: boolean;
};

type CreateNewMapProps = ModalProps<{
  createBlankMapErrorMessage: string;
  createMapFromInputProps: ReturnType<typeof useCreateMapFromInput>;
  createSpreadsheetMapErrorMessage: string;
  layeringAvailability: {
    isDisabled: boolean;
    isLoading: boolean;
  };
  isLoading: boolean;
  isNewSalesRepMap?: boolean;
  prefillFormData?: Partial<PrefillFormData>;

  onCreateBlankMap: (mapName: string, mapDescription: string) => void;
  onCreateMapSuccess: (mapId: number) => void;
  onCreateMapFromSpreadsheet: (
    file: File,
    mapName: string,
    mapDescription: string,
    onProgressChange: (progress: number) => void,
    onStatusChange: (status: UploadStatus, message?: string) => void
  ) => Promise<void>;
  onCreateSpreadsheetMapErrorMessageClose: () => void;
  onOpenLayerMapsModal: (props: CreateLayeredMapProps) => void;
  onStartGoogleSheetsClick: (mapName: string, mapDescription: string) => void;
}>;

export const CreateNewMapComponent: FC<CreateNewMapProps> = ({
  createBlankMapErrorMessage,
  createSpreadsheetMapErrorMessage,
  layeringAvailability,
  isLoading,
  isNewSalesRepMap,
  isOpen,
  onClose,
  onCreateBlankMap,
  onCreateMapFromSpreadsheet,
  onCreateMapSuccess,
  onCreateSpreadsheetMapErrorMessageClose,
  onOpenLayerMapsModal,
  onStartGoogleSheetsClick,
  prefillFormData,
  createMapFromInputProps: {
    isLoading: isCreateMapFromInputLoading,
    resetState: resetCreateFromInput,
    errorMessage: createFromInputError,
    createMapFromTextInput,
  },
}) => {
  const getPrefillValue = <K extends keyof PrefillFormData>(
    key: K, defaultValue: PrefillFormData[K]
  ): PrefillFormData[K] => (prefillFormData?.[key] ?? defaultValue);

  const [mapName, setMapName] = useState(getPrefillValue('mapName', ''));
  const [mapDescription, setMapDescription] = useState(getPrefillValue('mapDescription', ''));
  const [textInputValue, setTextInputValue] = useState(getPrefillValue('textInputValue', ''));
  const [isMapOverviewOpen, setIsMapOverviewOpen] = useState(getPrefillValue('isMapOverviewOpen', true));
  const [isDataSourceOpen, setIsDataSourceOpen] = useState(getPrefillValue('isDataSourceOpen', false));
  const [isMapCreationInProgress, setIsMapCreationInProgress] = useState(false);
  const [isSampleDataLoading, setIsSampleDataLoading] = useState(false);
  const [mapNameInputRef, setMapNameInputRef] = useHookWithRefCallback<HTMLInputElement>();
  const { replaceCurrentModal: replaceCurrentModalWithAppError } = useModal('AppError');
  const [t] = useTranslation();
  const isMobileScreen = useIsMobileScreenSelector();

  const toggleMapOverviewOpen = useCallback(() => {
    setIsMapOverviewOpen(isOpen => !isOpen);
  }, []);

  const onMapOverviewFormSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    setIsMapOverviewOpen(false);
    setIsDataSourceOpen(true);
  }, []);

  const onStartBlankClick = useCallback(() =>
    onCreateBlankMap(mapName, mapDescription),
  [mapDescription, mapName, onCreateBlankMap]);

  const onUploadItemSelect = useCallback((file: File, onProgressChange: (progress: number) => void, onStatusChange: (status: UploadStatus) => void) => {
    const onUploadStatusChange = (status: UploadStatus) => {
      setIsMapCreationInProgress(status === UploadStatus.InProgress);
      onStatusChange(status);
    };
    onCreateMapFromSpreadsheet(file, mapName, mapDescription, onProgressChange, onUploadStatusChange);
  },
  [mapDescription, mapName, onCreateMapFromSpreadsheet]);

  const handleCreateMapFromInputProps = useCallback(() => {
    createMapFromTextInput(
      textInputValue,
      mapName,
      mapDescription,
      onCreateMapSuccess,
    );
  }, [createMapFromTextInput, mapDescription, mapName, onCreateMapSuccess, textInputValue]);

  const handleStartGoogleSheetsClick = useCallback(() =>
    onStartGoogleSheetsClick(mapName, mapDescription),
  [mapDescription, mapName, onStartGoogleSheetsClick]);

  const handleUseSampleData = useCallback(() => {
    setIsSampleDataLoading(true);
    fetch(SampleSpreadsheetUrl)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], SAMPLE_SPREADSHEET_FILE_NAME, { type: blob.type });
        const effectiveMapName = isTextEmpty(mapName) ? SAMPLE_MAP_DEFAULT_NAME : mapName;
        return onCreateMapFromSpreadsheet(file, effectiveMapName, mapDescription, () => null, (status, message) => {
          if (status === UploadStatus.Error) {
            replaceCurrentModalWithAppError({ appError: {
              type: AppErrorType.General,
              title: t('Could not create map from sample data'),
              text: message ?? t('An unknown error has occurred.'),
            } });
          }
        });
      })
      .catch(() => {
        const appError: AppError = {
          type: AppErrorType.General,
          title: t('Could not create map from sample data'),
          text: t('An unknown error has occurred.'),
        };
        replaceCurrentModalWithAppError({ appError });
      });
  }, [mapDescription, mapName, onCreateMapFromSpreadsheet, replaceCurrentModalWithAppError, t]);

  useEffect(() => {
    if (isTextEmpty(mapName)) {
      setIsMapOverviewOpen(true);
      setIsDataSourceOpen(false);
    }
  }, [mapName]);

  useEffect(() => {
    if (!isOpen) {
      setMapName('');
      setMapDescription('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && mapNameInputRef) {
      mapNameInputRef.focus();
    }
  }, [isOpen, mapNameInputRef]);

  useEffect(() => {
    resetCreateFromInput();
    // textInputValue among dependencies as we want to clear that error when user starts changing the text again
  }, [textInputValue, resetCreateFromInput]);

  const isAnyLoadingInProgress = isMapCreationInProgress || isLoading || isSampleDataLoading;
  const isConfirmButtonDisabled = (textInputValue.length === 0) || isAnyLoadingInProgress;

  const ModalConfirmButton = useMemo(() => (
    <ButtonComponent
      prefixIcon={faMap}
      text={t('Map Now')}
      isDisabled={isConfirmButtonDisabled}
      onClick={isConfirmButtonDisabled ? undefined : handleCreateMapFromInputProps}
    />
  ), [handleCreateMapFromInputProps, isConfirmButtonDisabled, t]);

  const isContinueEnabled = !isTextEmpty(mapName);

  const handleLayeredMapClick = useCallback(() =>
    onOpenLayerMapsModal({
      initialData: { mapName, mapDescription },
    }),
  [onOpenLayerMapsModal, mapName, mapDescription]);

  const handleClose = useCallback(() => {
    setIsSampleDataLoading(false);
    onClose();
  }, [onClose]);

  return (
    <ModalComponent
      additionalContent={isSampleDataLoading ? <OverlayLoaderComponent /> : null}
      onClose={handleClose}
      isOpen={isOpen}
      caption={isNewSalesRepMap ? t('map.salesReps.createTitle') : t('Create New Map')}
      leftFooterContent={<ModalFooterSampleExcelComponent onUseSampleData={handleUseSampleData} isDisabled={isAnyLoadingInProgress} />}
      confirmButton={ModalConfirmButton}
      contentStyle={css({ padding: 0 })}
      isCloseDisabled={isMapCreationInProgress || isSampleDataLoading}
    >
      <OutlinePanelComponent outlines={[OutlinePanelOptions.Top]}>
        <Accordion>
          <Accordion.Item
            open={isMapOverviewOpen}
            isLocked={!isContinueEnabled}
            onToggle={isContinueEnabled ? toggleMapOverviewOpen : undefined}
          >
            <Accordion.ItemHeading css={itemButtonStyle}>
              <Accordion.StepNumber />
              {isNewSalesRepMap ? t('map.salesReps.nameYourMap') : t('Name Your Map')}
            </Accordion.ItemHeading>
            <Accordion.Panel>
              <MapNameAndDescriptionComponent
                alignContinueButtonToRight={false}
                mapName={mapName}
                mapDescription={mapDescription}
                isContinueEnabled={isContinueEnabled}
                setMapNameInputRef={setMapNameInputRef}
                setMapName={setMapName}
                setMapDescription={setMapDescription}
                onSubmit={onMapOverviewFormSubmit}
                isNewSalesRepMap={isNewSalesRepMap}
              />
            </Accordion.Panel>
          </Accordion.Item>

          <Accordion.Item
            open={isDataSourceOpen}
            isLocked
          >
            <Accordion.ItemHeading css={itemButtonStyle}>
              <Accordion.StepNumber />
              {isNewSalesRepMap ? t('map.salesReps.selectDataSource') : t('Select Data Source')}
            </Accordion.ItemHeading>
            <Accordion.Panel>
              <div css={accordionContentStyle}>
                <DataSourceNotesComponent isNewSalesRepMap={isNewSalesRepMap} />

                <SelectSpreadsheetWithErrorComponent
                  error={createSpreadsheetMapErrorMessage}
                  onErrorMessageClose={onCreateSpreadsheetMapErrorMessageClose}
                  isLoading={isLoading}
                  onSelect={onUploadItemSelect}
                />

                {createBlankMapErrorMessage && (
                  <AlertComponent
                    css={alertStyle}
                    type="danger"
                  >
                    {createBlankMapErrorMessage}
                  </AlertComponent>
                )}

                <OrSeparator />

                <TextInputSpreadsheetDataComponent
                  isLoading={isCreateMapFromInputLoading}
                  errorMessage={createFromInputError}
                  value={textInputValue}
                  onInputChange={setTextInputValue}
                />

                <OrSeparator />

                <UploadMethodsComponent
                  isLoading={isLoading}
                  isMobileScreen={isMobileScreen}
                  onStartBlankClick={onStartBlankClick}
                  onStartGoogleSheetsClick={handleStartGoogleSheetsClick}
                />

                {!isMobileScreen && (
                  <>
                    <OrSeparator />

                    <div css={layeringWrapper}>
                      {layeringAvailability.isLoading && (
                        <OverlayLoaderComponent
                          loaderType={LoaderType.Default}
                          loaderSize={computeButtonFontSize({ size: defaultButtonSize }) + LOADER_SIZE_OFFSET}
                        />
                      )}
                      <TooltipComponent
                        behavior={TooltipBehavior.ShowOnHover}
                        delayShow={0.5}
                        tooltipContent={layeringAvailability.isDisabled ? t('layeredMap.creation.requiresTwoMaps') : undefined}
                      >
                        <ButtonComponent
                          size={defaultButtonSize}
                          buttonStyle={ButtonStyle.Secondary}
                          css={fullWidthStyle}
                          isDisabled={layeringAvailability.isDisabled || layeringAvailability.isLoading}
                          onClick={handleLayeredMapClick}
                          text={t('layeredMap.creation.layer')}
                        />
                      </TooltipComponent>
                    </div>
                  </>
                )}
              </div>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </OutlinePanelComponent>
    </ModalComponent>
  );
};

const OrSeparator = () => {
  const [t] = useTranslation();
  return <span css={uploadMethodSeparatorStyle}>{t('or')}</span>;
};
